import { useState, useCallback, useMemo } from "react";
import { OrderType } from "../../../../services";
import {
  TRANSACTION_TYPES,
  FILTER_OPTIONS,
  FILTER_MONTHS,
} from "../../../Transactions/constants";
import {
  Filters,
  SortConfig,
  SortValueType,
} from "../../../Transactions/types";
import { StringOptions } from "../../../../components/Select";

export interface UseTransactionFiltersProps {
  initialYear?: StringOptions;
  initialDateRange?: StringOptions;
  initialTransactionType?: StringOptions;
  initialFilters?: Partial<Filters>;
  onFilterChange?: (filters: Filters) => void;
}

export interface UseTransactionFiltersReturn {
  filters: Filters;
  sortConfig: SortConfig;
  setFilters: (filters: Filters | ((prev: Filters) => Filters)) => void;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  handlePageChange: (newPage: number) => void;
  handleRowsPerPageChange: (newRowsPerPage: number) => void;
  handleSortChange: (field: any, order: any) => void;
}

const getDefaultFilters = (): Filters => ({
  searchBy: "",
  transactionType: TRANSACTION_TYPES[0],
  dateRange: FILTER_OPTIONS[0],
  month: FILTER_MONTHS[0],
  year: {
    label: String(new Date().getFullYear()),
    value: String(new Date().getFullYear()),
  },
  page: 0,
  rowsPerPage: 10,
  category: null,
});

/**
 * Custom hook to manage transaction filters
 */
export const useTransactionFilters = ({
  initialYear,
  initialDateRange,
  initialTransactionType,
  initialFilters,
  onFilterChange,
}: UseTransactionFiltersProps = {}): UseTransactionFiltersReturn => {
  const [filters, setFilters] = useState<Filters>(() => ({
    ...getDefaultFilters(),
    ...(initialYear && { year: initialYear }),
    ...(initialDateRange && { dateRange: initialDateRange }),
    ...(initialTransactionType && { transactionType: initialTransactionType }),
    ...initialFilters,
  }));

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: SortValueType.TRANSACTION_DATE,
    order: OrderType.DESCENDING,
  });

  const handleSetFilters = useCallback(
    (newFilters: Filters | ((prev: Filters) => Filters)) => {
      setFilters((prev) => {
        const finalFilters =
          typeof newFilters === "function" ? newFilters(prev) : newFilters;
        if (onFilterChange) {
          onFilterChange(finalFilters);
        }
        return finalFilters;
      });
    },
    [onFilterChange],
  );

  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchBy !== "" ||
      filters.transactionType.value !== TRANSACTION_TYPES[0].value ||
      filters.dateRange.value !== FILTER_OPTIONS[0].value ||
      filters.category !== null
    );
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    if (onFilterChange) {
      onFilterChange(defaultFilters);
    }
  }, [onFilterChange]);

  const handlePageChange = useCallback((newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setFilters((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  }, []);

  const handleSortChange = useCallback((field: any, order: any) => {
    setSortConfig({ field, order });
  }, []);

  return {
    filters,
    sortConfig,
    setFilters: handleSetFilters,
    hasActiveFilters,
    clearAllFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
  };
};
